/**
 * EmptyState — shared component for empty list/table/page states.
 * Always renders an icon (or fallback), a required message, an optional
 * description, and an optional action button.
 * Satisfies WCAG 2.2 AA: status is communicated via text, not color alone.
 */

import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import InboxIcon from '@mui/icons-material/Inbox';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useTheme } from '@mui/material/styles';

interface EmptyStateProps {
  /** Icon to display. Defaults by `role` — an inbox for `status`, an error mark for `alert`. */
  icon?: React.ReactNode;
  /** Primary message — e.g. "No Documents Found" */
  message: string;
  /** Secondary descriptive text shown below the message. */
  description?: string;
  /** Optional call-to-action element — e.g. an AppButton. */
  action?: React.ReactNode;
  /** Minimum height of the container. Useful for table cells. Defaults to 160px. */
  minHeight?: number | string;
  /**
   * Live-region politeness. Defaults to `status` (polite). Use `alert` when the state reports a
   * failure rather than an ordinary absence of data, so it is announced without waiting.
   */
  role?: 'status' | 'alert';
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  message,
  description,
  action,
  minHeight = 160,
  role = 'status',
}) => {
  const theme = useTheme();

  return (
    <Box
      role={role}
      aria-label={message}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight,
        gap: 1,
        py: 4,
        color: theme.palette.text.secondary,
      }}
    >
      <Box
        sx={{
          fontSize: 48,
          lineHeight: 1,
          // A failure should not look like an ordinary absence of data.
          color: role === 'alert' ? theme.palette.error.main : theme.palette.grey[400],
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {icon ??
          (role === 'alert' ? (
            <ErrorOutlineIcon sx={{ fontSize: 48 }} />
          ) : (
            <InboxIcon sx={{ fontSize: 48 }} />
          ))}
      </Box>

      <Typography variant="h6" component="p" color="text.secondary" align="center">
        {message}
      </Typography>

      {description && (
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{ maxWidth: 400 }}
        >
          {description}
        </Typography>
      )}

      {action && <Box sx={{ mt: 1 }}>{action}</Box>}
    </Box>
  );
};
